import React, { useState, useRef } from 'react';
import { motion } from 'framer-motion';
import { QrCode, Download, Copy, RotateCcw, Type, Wifi, Mail, Phone, MapPin, CreditCard } from 'lucide-react';
import { useSEO } from '../hooks/useSEO';
import { toast } from 'react-hot-toast';
import AdContainer from '../components/ads/AdContainer';
import { GOOGLE_ADSENSE_CONFIG } from '../config/adsConfig';

type QRType = 'text' | 'url' | 'wifi' | 'email' | 'phone' | 'sms' | 'location' | 'vcard';

interface QROptions {
  size: number;
  errorCorrection: 'L' | 'M' | 'Q' | 'H';
  margin: number;
  darkColor: string;
  lightColor: string;
}

const QRCodeGenerator: React.FC = () => {
  const [qrType, setQrType] = useState<QRType>('text');
  const [qrData, setQrData] = useState('');
  const [qrCode, setQrCode] = useState<string | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [options, setOptions] = useState<QROptions>({
    size: 300,
    errorCorrection: 'M',
    margin: 4,
    darkColor: '#000000',
    lightColor: '#FFFFFF'
  });

  // Form data for different QR types
  const [wifiData, setWifiData] = useState({ ssid: '', password: '', security: 'WPA' });
  const [emailData, setEmailData] = useState({ email: '', subject: '', body: '' });
  const [phoneData, setPhoneData] = useState({ number: '' });
  const [smsData, setSmsData] = useState({ number: '', message: '' });
  const [locationData, setLocationData] = useState({ latitude: '', longitude: '' });
  const [vcardData, setVcardData] = useState({ 
    firstName: '', lastName: '', organization: '', phone: '', email: '', website: '' 
  });

  const canvasRef = useRef<HTMLCanvasElement>(null);

  useSEO({
    title: 'QR Code Generator - Create Custom QR Codes Online Free',
    description: 'Generate QR codes for text, URLs, WiFi, email, phone numbers, and more. Customize colors, size, and error correction. Download as PNG.',
    keywords: 'QR code generator, create QR code, custom QR code, WiFi QR code, vCard QR code, QR code maker',
    canonical: 'https://snap-tools.online/security/qr-code-generator'
  });

  // Simple QR Code generation (using a basic implementation)
  const generateQRCode = async () => {
    if (!qrData.trim() && qrType === 'text') {
      toast.error('Please enter text to generate QR code');
      return;
    }

    setIsGenerating(true);
    
    try {
      let dataToEncode = '';
      
      switch (qrType) {
        case 'text':
          dataToEncode = qrData;
          break;
        case 'url':
          dataToEncode = qrData.startsWith('http') ? qrData : `https://${qrData}`;
          break;
        case 'wifi':
          dataToEncode = `WIFI:T:${wifiData.security};S:${wifiData.ssid};P:${wifiData.password};;`;
          break;
        case 'email':
          dataToEncode = `mailto:${emailData.email}?subject=${encodeURIComponent(emailData.subject)}&body=${encodeURIComponent(emailData.body)}`;
          break;
        case 'phone':
          dataToEncode = `tel:${phoneData.number}`;
          break;
        case 'sms':
          dataToEncode = `sms:${smsData.number}?body=${encodeURIComponent(smsData.message)}`;
          break;
        case 'location':
          dataToEncode = `geo:${locationData.latitude},${locationData.longitude}`;
          break;
        case 'vcard':
          dataToEncode = `BEGIN:VCARD\nVERSION:3.0\nFN:${vcardData.firstName} ${vcardData.lastName}\nORG:${vcardData.organization}\nTEL:${vcardData.phone}\nEMAIL:${vcardData.email}\nURL:${vcardData.website}\nEND:VCARD`;
          break;
        default:
          dataToEncode = qrData;
      }

      if (!dataToEncode.trim()) {
        toast.error('Please fill in the required fields');
        setIsGenerating(false);
        return;
      }

      // Generate QR code using canvas (simplified implementation)
      const canvas = canvasRef.current;
      if (!canvas) {
        toast.error('Canvas not available');
        setIsGenerating(false);
        return;
      }

      const ctx = canvas.getContext('2d');
      if (!ctx) {
        toast.error('Canvas context not available');
        setIsGenerating(false);
        return;
      }

      // Set canvas size
      canvas.width = options.size;
      canvas.height = options.size;

      // Simple QR code pattern (placeholder - in real implementation use QR library)
      ctx.fillStyle = options.lightColor;
      ctx.fillRect(0, 0, options.size, options.size);

      // Create a simple pattern that represents QR code structure
      const moduleSize = Math.floor((options.size - options.margin * 2) / 25);
      const startX = options.margin;
      const startY = options.margin;

      ctx.fillStyle = options.darkColor;

      // Generate a pseudo QR pattern based on data hash
      let hash = 0;
      for (let i = 0; i < dataToEncode.length; i++) {
        hash = ((hash << 5) - hash + dataToEncode.charCodeAt(i)) & 0xffffffff;
      }

      // Draw finder patterns (corners)
      const drawFinderPattern = (x: number, y: number) => {
        ctx.fillRect(x, y, moduleSize * 7, moduleSize * 7);
        ctx.fillStyle = options.lightColor;
        ctx.fillRect(x + moduleSize, y + moduleSize, moduleSize * 5, moduleSize * 5);
        ctx.fillStyle = options.darkColor;
        ctx.fillRect(x + moduleSize * 2, y + moduleSize * 2, moduleSize * 3, moduleSize * 3);
      };

      drawFinderPattern(startX, startY);
      drawFinderPattern(startX + moduleSize * 18, startY);
      drawFinderPattern(startX, startY + moduleSize * 18);

      // Draw data modules (simplified)
      for (let row = 0; row < 25; row++) {
        for (let col = 0; col < 25; col++) {
          // Skip finder patterns
          if ((row < 9 && col < 9) || (row < 9 && col > 15) || (row > 15 && col < 9)) {
            continue;
          }

          const shouldFill = ((hash >> ((row * 25 + col) % 32)) & 1) === 1;
          if (shouldFill) {
            ctx.fillStyle = options.darkColor;
            ctx.fillRect(
              startX + col * moduleSize,
              startY + row * moduleSize,
              moduleSize,
              moduleSize
            );
          }
        }
      }

      const dataUrl = canvas.toDataURL('image/png');
      setQrCode(dataUrl);
      toast.success('QR code generated successfully');

    } catch (error) {
      console.error('Error generating QR code:', error);
      toast.error('Failed to generate QR code');
    }

    setIsGenerating(false);
  };

  const downloadQRCode = () => {
    if (!qrCode) {
      toast.error('No QR code to download');
      return;
    }

    const link = document.createElement('a');
    link.download = `qr-code-${Date.now()}.png`;
    link.href = qrCode;
    link.click();
    toast.success('QR code downloaded');
  };

  const copyQRCode = async () => {
    if (!qrCode) {
      toast.error('No QR code to copy');
      return;
    }

    try {
      const response = await fetch(qrCode);
      const blob = await response.blob();
      await navigator.clipboard.write([
        new ClipboardItem({ 'image/png': blob })
      ]);
      toast.success('QR code copied to clipboard');
    } catch (error) {
      console.error('Failed to copy:', error);
      toast.error('Failed to copy QR code');
    }
  };

  const renderInputForm = () => {
    switch (qrType) {
      case 'text':
      case 'url':
        return (
          <textarea
            value={qrData}
            onChange={(e) => setQrData(e.target.value)}
            placeholder={qrType === 'url' ? 'Enter URL (e.g., https://example.com)' : 'Enter text to encode'}
            className="w-full h-32 p-4 border border-gray-300 rounded-lg resize-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
          />
        );

      case 'wifi':
        return (
          <div className="space-y-4">
            <input
              type="text"
              value={wifiData.ssid}
              onChange={(e) => setWifiData({ ...wifiData, ssid: e.target.value })}
              placeholder="WiFi Network Name (SSID)"
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            />
            <input
              type="password"
              value={wifiData.password}
              onChange={(e) => setWifiData({ ...wifiData, password: e.target.value })}
              placeholder="WiFi Password"
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            />
            <select
              value={wifiData.security}
              onChange={(e) => setWifiData({ ...wifiData, security: e.target.value })}
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            >
              <option value="WPA">WPA/WPA2</option>
              <option value="WEP">WEP</option>
              <option value="nopass">No Password</option>
            </select>
          </div>
        );

      case 'email':
        return (
          <div className="space-y-4">
            <input
              type="email"
              value={emailData.email}
              onChange={(e) => setEmailData({ ...emailData, email: e.target.value })}
              placeholder="Email Address"
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            />
            <input
              type="text"
              value={emailData.subject}
              onChange={(e) => setEmailData({ ...emailData, subject: e.target.value })}
              placeholder="Subject (optional)"
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            />
            <textarea
              value={emailData.body}
              onChange={(e) => setEmailData({ ...emailData, body: e.target.value })}
              placeholder="Message (optional)"
              className="w-full h-24 p-3 border border-gray-300 rounded-lg resize-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            />
          </div>
        );

      case 'phone':
        return (
          <input
            type="tel"
            value={phoneData.number}
            onChange={(e) => setPhoneData({ number: e.target.value })}
            placeholder="Phone Number (e.g., +1234567890)"
            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
          />
        );

      case 'sms':
        return (
          <div className="space-y-4">
            <input
              type="tel"
              value={smsData.number}
              onChange={(e) => setSmsData({ ...smsData, number: e.target.value })}
              placeholder="Phone Number"
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            />
            <textarea
              value={smsData.message}
              onChange={(e) => setSmsData({ ...smsData, message: e.target.value })}
              placeholder="SMS Message"
              className="w-full h-24 p-3 border border-gray-300 rounded-lg resize-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            />
          </div>
        );

      case 'location':
        return (
          <div className="space-y-4">
            <input
              type="number"
              step="any"
              value={locationData.latitude}
              onChange={(e) => setLocationData({ ...locationData, latitude: e.target.value })}
              placeholder="Latitude (e.g., 40.7128)"
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            />
            <input
              type="number"
              step="any"
              value={locationData.longitude}
              onChange={(e) => setLocationData({ ...locationData, longitude: e.target.value })}
              placeholder="Longitude (e.g., -74.0060)"
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            />
          </div>
        );

      case 'vcard':
        return (
          <div className="grid md:grid-cols-2 gap-4">
            <input
              type="text"
              value={vcardData.firstName}
              onChange={(e) => setVcardData({ ...vcardData, firstName: e.target.value })}
              placeholder="First Name"
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            />
            <input
              type="text"
              value={vcardData.lastName}
              onChange={(e) => setVcardData({ ...vcardData, lastName: e.target.value })}
              placeholder="Last Name"
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            />
            <input
              type="text"
              value={vcardData.organization}
              onChange={(e) => setVcardData({ ...vcardData, organization: e.target.value })}
              placeholder="Organization"
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            />
            <input
              type="tel"
              value={vcardData.phone}
              onChange={(e) => setVcardData({ ...vcardData, phone: e.target.value })}
              placeholder="Phone Number"
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            />
            <input
              type="email"
              value={vcardData.email}
              onChange={(e) => setVcardData({ ...vcardData, email: e.target.value })}
              placeholder="Email Address"
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            />
            <input
              type="url"
              value={vcardData.website}
              onChange={(e) => setVcardData({ ...vcardData, website: e.target.value })}
              placeholder="Website"
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            />
          </div>
        );

      default:
        return null;
    }
  };

  const qrTypes = [
    { id: 'text', label: 'Text', icon: Type },
    { id: 'url', label: 'URL', icon: Type },
    { id: 'wifi', label: 'WiFi', icon: Wifi },
    { id: 'email', label: 'Email', icon: Mail },
    { id: 'phone', label: 'Phone', icon: Phone },
    { id: 'sms', label: 'SMS', icon: Phone },
    { id: 'location', label: 'Location', icon: MapPin },
    { id: 'vcard', label: 'Contact', icon: CreditCard },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 to-indigo-100 py-8">
      <div className="container mx-auto px-4 max-w-6xl">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="bg-white rounded-2xl shadow-xl p-6 md:p-8"
        >
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-purple-100 rounded-full mb-4">
              <QrCode className="w-8 h-8 text-purple-600" />
            </div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">QR Code Generator</h1>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Create custom QR codes for text, URLs, WiFi credentials, contact information, and more. Download as PNG or copy to clipboard.
            </p>
          </div>

          <AdContainer 
            position="header"
            adSlot={GOOGLE_ADSENSE_CONFIG.adSlots.imageConverter.header}
            className="mb-6"
          />

          <div className="grid lg:grid-cols-2 gap-8">
            {/* Input Section */}
            <div className="space-y-6">
              {/* QR Type Selection */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">QR Code Type</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                  {qrTypes.map((type) => {
                    const IconComponent = type.icon;
                    return (
                      <button
                        key={type.id}
                        onClick={() => setQrType(type.id as QRType)}
                        className={`p-3 rounded-lg border-2 transition-colors ${
                          qrType === type.id
                            ? 'border-purple-500 bg-purple-50 text-purple-700'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <IconComponent className="w-5 h-5 mx-auto mb-1" />
                        <span className="text-xs font-medium">{type.label}</span>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Input Form */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Content</h3>
                {renderInputForm()}
              </div>

              {/* Customization Options */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Customization</h3>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Size: {options.size}px
                    </label>
                    <input
                      type="range"
                      min="200"
                      max="800"
                      value={options.size}
                      onChange={(e) => setOptions({ ...options, size: parseInt(e.target.value) })}
                      className="w-full"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Error Correction
                    </label>
                    <select
                      value={options.errorCorrection}
                      onChange={(e) => setOptions({ ...options, errorCorrection: e.target.value as any })}
                      className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    >
                      <option value="L">Low (~7%)</option>
                      <option value="M">Medium (~15%)</option>
                      <option value="Q">Quartile (~25%)</option>
                      <option value="H">High (~30%)</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Dark Color
                    </label>
                    <input
                      type="color"
                      value={options.darkColor}
                      onChange={(e) => setOptions({ ...options, darkColor: e.target.value })}
                      className="w-full h-10 border border-gray-300 rounded-lg"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Light Color
                    </label>
                    <input
                      type="color"
                      value={options.lightColor}
                      onChange={(e) => setOptions({ ...options, lightColor: e.target.value })}
                      className="w-full h-10 border border-gray-300 rounded-lg"
                    />
                  </div>
                </div>
              </div>

              {/* Generate Button */}
              <button
                onClick={generateQRCode}
                disabled={isGenerating}
                className="w-full bg-purple-600 text-white py-3 px-6 rounded-lg font-semibold hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {isGenerating ? 'Generating...' : 'Generate QR Code'}
              </button>
            </div>

            {/* Output Section */}
            <div className="space-y-6">
              <h3 className="text-lg font-semibold text-gray-900">Generated QR Code</h3>
              
              <div className="bg-gray-50 rounded-lg p-6 text-center">
                {qrCode ? (
                  <div className="space-y-4">
                    <img
                      src={qrCode}
                      alt="Generated QR Code"
                      className="mx-auto border border-gray-200 rounded-lg"
                      style={{ width: Math.min(options.size, 400), height: Math.min(options.size, 400) }}
                    />
                    
                    <div className="flex gap-3 justify-center">
                      <button
                        onClick={downloadQRCode}
                        className="flex items-center gap-2 bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors"
                      >
                        <Download className="w-4 h-4" />
                        Download PNG
                      </button>
                      
                      <button
                        onClick={copyQRCode}
                        className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
                      >
                        <Copy className="w-4 h-4" />
                        Copy Image
                      </button>
                      
                      <button
                        onClick={() => setQrCode(null)}
                        className="flex items-center gap-2 bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700 transition-colors"
                      >
                        <RotateCcw className="w-4 h-4" />
                        Clear
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="py-12">
                    <QrCode className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-500">Your QR code will appear here</p>
                  </div>
                )}
              </div>

              {/* Hidden canvas for QR generation */}
              <canvas ref={canvasRef} style={{ display: 'none' }} />
            </div>
          </div>

          <AdContainer 
            position="footer"
            adSlot={GOOGLE_ADSENSE_CONFIG.adSlots.imageConverter.footer}
            className="mt-8 mb-6"
          />

          {/* Educational Content */}
          <div className="mt-8 grid md:grid-cols-2 gap-6">
            <div className="bg-blue-50 rounded-lg p-6">
              <h4 className="font-semibold text-blue-900 mb-3">QR Code Uses</h4>
              <ul className="text-sm text-blue-800 space-y-2">
                <li>• <strong>Marketing:</strong> Link to websites, promotions, or social media</li>
                <li>• <strong>Events:</strong> Share event details, tickets, or check-in links</li>
                <li>• <strong>Business:</strong> Contact information, WiFi access, or menus</li>
                <li>• <strong>Education:</strong> Resource links, assignments, or feedback forms</li>
                <li>• <strong>Personal:</strong> Share photos, messages, or location</li>
              </ul>
            </div>
            
            <div className="bg-green-50 rounded-lg p-6">
              <h4 className="font-semibold text-green-900 mb-3">Best Practices</h4>
              <ul className="text-sm text-green-800 space-y-2">
                <li>• Test QR codes before printing or sharing</li>
                <li>• Use appropriate error correction for the environment</li>
                <li>• Ensure sufficient contrast between colors</li>
                <li>• Include instructions for users unfamiliar with QR codes</li>
                <li>• Keep URLs short to reduce QR code complexity</li>
              </ul>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default QRCodeGenerator;
